import {
  Ban,
  Copy,
  DollarSign,
  Download,
  Edit,
  Eye,
  Mail,
  Printer,
  Send,
  Trash2,
} from 'lucide-react';
import type { RowActionItem, RowMenuEntry } from '../components/transactions/RowActionsMenu';
import type { SalesTransaction, SalesTransactionAction } from '../types/salesTransaction';
import { canEditSalesTransaction } from './invoiceEditability';
import {
  canCopyInvoice,
  canDeleteOrVoidSales,
  canManageInvoices,
} from './salesTransactionPermissions';

export interface SalesInvoiceRowActionHandlers {
  onView: () => void;
  onEdit: () => void;
  onPost: () => void;
  onDelete: () => void;
  onVoid: () => void;
  onReceivePayment: () => void;
  onPrint: () => void;
  onDownloadPdf: () => void;
  onSend: () => void;
  onCopy: () => void;
}

export interface SalesInvoiceRowActionsLayout {
  inlineActions: RowActionItem[];
  menuEntries: RowMenuEntry[];
}

const hasAction = (txn: SalesTransaction, action: SalesTransactionAction) =>
  txn.available_actions.includes(action);

const buildActionCatalog = (
  txn: SalesTransaction,
  handlers: SalesInvoiceRowActionHandlers
): Map<string, RowActionItem> => {
  const catalog = new Map<string, RowActionItem>();
  const canManage = canManageInvoices();
  const canVoidOrDelete = canDeleteOrVoidSales();
  const canEdit = canEditSalesTransaction(txn) && canManage;

  if (hasAction(txn, 'view')) {
    catalog.set('view', {
      id: 'view',
      label: 'View',
      icon: Eye,
      onClick: handlers.onView,
    });
  }

  if (canEdit) {
    catalog.set('edit', {
      id: 'edit',
      label: 'Edit',
      icon: Edit,
      onClick: handlers.onEdit,
    });
  }

  if (hasAction(txn, 'post') && canManage) {
    catalog.set('post', {
      id: 'post',
      label: 'Post',
      icon: Send,
      variant: 'primary',
      showLabel: true,
      onClick: handlers.onPost,
    });
  }

  if (hasAction(txn, 'receive_payment')) {
    catalog.set('receive_payment', {
      id: 'receive_payment',
      label: 'Pay',
      icon: DollarSign,
      variant: 'primary',
      showLabel: true,
      onClick: handlers.onReceivePayment,
    });
  }

  catalog.set('print', {
    id: 'print',
    label: 'Print',
    icon: Printer,
    onClick: handlers.onPrint,
  });

  catalog.set('download_pdf', {
    id: 'download_pdf',
    label: 'Download',
    icon: Download,
    onClick: handlers.onDownloadPdf,
  });

  if (canManage) {
    catalog.set('send', {
      id: 'send',
      label: 'Send email',
      icon: Mail,
      onClick: handlers.onSend,
    });
  }

  if (canCopyInvoice()) {
    catalog.set('copy', {
      id: 'copy',
      label: 'Copy',
      icon: Copy,
      onClick: handlers.onCopy,
    });
  }

  if (hasAction(txn, 'delete') && canVoidOrDelete) {
    catalog.set('delete', {
      id: 'delete',
      label: 'Delete draft',
      icon: Trash2,
      variant: 'destructive',
      onClick: handlers.onDelete,
    });
  }

  if (hasAction(txn, 'void') && canVoidOrDelete) {
    catalog.set('void', {
      id: 'void',
      label: 'Void',
      icon: Ban,
      variant: 'destructive',
      onClick: handlers.onVoid,
    });
  }

  return catalog;
};

const getInlineActionIds = (catalog: Map<string, RowActionItem>): string[] => {
  const inline: string[] = [];

  if (catalog.has('post')) inline.push('post');
  if (catalog.has('receive_payment')) inline.push('receive_payment');
  if (catalog.has('edit') && inline.length < 2) inline.push('edit');
  if (inline.length === 0 && catalog.has('view')) inline.push('view');

  return inline;
};

const MENU_GROUPS: string[][] = [
  ['view', 'edit', 'post', 'receive_payment'],
  ['print', 'download_pdf', 'send', 'copy'],
  ['delete', 'void'],
];

/** Primary actions inline; remaining actions grouped in overflow menu. */
export const buildSalesInvoiceRowActions = (
  txn: SalesTransaction,
  handlers: SalesInvoiceRowActionHandlers
): SalesInvoiceRowActionsLayout => {
  const catalog = buildActionCatalog(txn, handlers);
  const inlineIds = new Set(getInlineActionIds(catalog));

  const inline = getInlineActionIds(catalog)
    .map((id) => catalog.get(id))
    .filter((item): item is RowActionItem => item != null);

  const menu: RowMenuEntry[] = [];

  for (const group of MENU_GROUPS) {
    const groupItems = group
      .filter((id) => !inlineIds.has(id) && catalog.has(id))
      .map((id) => catalog.get(id)!);

    if (groupItems.length === 0) continue;
    if (menu.length > 0) menu.push({ type: 'separator' });
    menu.push(...groupItems);
  }

  return { inlineActions: inline, menuEntries: menu };
};
